/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Send from a journey", description = "Add a message tile to a journey and understand what differs from a campaign send.", lastUpdatedDate = new Date(1791331200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Send from a journey",
        description: "Add a message tile to a journey and understand what differs from a campaign send.",
        lastUpdatedDate: "Wed Oct 07 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "When to use a journey instead of a campaign",
          "depth": 0,
          "id": "when-to-use-a-journey-instead-of-a-campaign"
        }, {
          "value": "Add an email to a journey",
          "depth": 0,
          "id": "add-an-email-to-a-journey"
        }, {
          "value": "What works the same",
          "depth": 0,
          "id": "what-works-the-same"
        }, {
          "value": "What's different",
          "depth": 0,
          "id": "whats-different"
        }, {
          "value": "Subsets on the parent model",
          "depth": 0,
          "id": "subsets-on-the-parent-model"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      h2: "h2",
      ol: "ol",
      li: "li",
      img: "img",
      ul: "ul"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers, lifecycle and CRM teams"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsxs(_components.td, {
              children: ["An email built in ", _jsx(_components.a, {
                href: "/native-delivery/emails",
                children: "Build an email"
              }), ", and a journey"]
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "A journey sends an email when someone reaches a Send email tile in it. You build the email the same way you would for a campaign, and the journey decides who receives it and when."
        })
      }), "\n", _jsx(_components.h2, {
        id: "when-to-use-a-journey-instead-of-a-campaign",
        children: "When to use a journey instead of a campaign"
      }), "\n", _jsxs(_components.p, {
        children: ["Use a journey when the email belongs to a sequence: a welcome series, a win-back, an abandoned-cart follow-up. The journey handles entry conditions, waits, and branching, and each ", _jsx(_components.strong, {
          children: "Send email"
        }), " tile sends one email."]
      }), "\n", _jsx(_components.p, {
        children: "A journey is also how you send after a wait longer than 7 days, which is the limit for a single campaign send."
      }), "\n", _jsx(_components.h2, {
        id: "add-an-email-to-a-journey",
        children: "Add an email to a journey"
      }), "\n", _jsxs(_components.p, {
        children: ["The email is built exactly as it is for a campaign. If you haven't built one yet, start with ", _jsx(_components.a, {
          href: "/native-delivery/emails",
          children: "Build an email"
        }), " and come back here to place it in the journey."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Open your journey and add a ", _jsx(_components.strong, {
            children: "Send email"
          }), " tile where you want the email to go."]
        }), "\n", _jsx(_components.li, {
          children: "Give the tile a name. This becomes the name of the message. It's what you'll look for in reporting."
        }), "\n", _jsxs(_components.li, {
          children: ["Add content, either ", _jsx(_components.strong, {
            children: "Choose a template"
          }), " or ", _jsx(_components.strong, {
            children: "Start from scratch"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Set the subject and preheader, then build the body. See ", _jsx(_components.a, {
            href: "/native-delivery/emails",
            children: "Build an email"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Personalize the content. Alongside profile attributes, a journey message can use properties of the event that triggered the journey. See ", _jsx(_components.a, {
            href: "/native-delivery/personalization",
            children: "Personalize a message"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Preview it against a real profile and send yourself a test. See ", _jsx(_components.a, {
            href: "/native-delivery/preview-and-test",
            children: "Preview and test"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Set send limits if you need them."
        }), "\n", _jsx(_components.li, {
          children: "Publish the journey."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "On the Send email tile, you set the email's metadata, send limits, and content."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "native-delivery/journeys/send-email-tile.png",
          alt: "The Send email tile: email metadata, send limits, and content"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Select ", _jsx(_components.strong, {
          children: "Choose a template"
        }), " or ", _jsx(_components.strong, {
          children: "Start from scratch"
        }), " to build the body in the drag-and-drop editor. An email needs a footer before the journey can activate."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "native-delivery/journeys/email-builder.png",
          alt: "Building the email body in the drag-and-drop editor"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["For building the journey itself, including entry conditions, waits, and branching, see the ", _jsx(_components.a, {
          href: "/customer-studio/journeys",
          children: "Journeys documentation"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "what-works-the-same",
        children: "What works the same"
      }), "\n", _jsx(_components.p, {
        children: "Content, personalization, preview and test, and send limits all behave as they do for a campaign. Subscription topics, unsubscribes, bounces, and suppression lists are enforced the same way."
      }), "\n", _jsx(_components.p, {
        children: "One addition: a journey message can personalize on the event that triggered the journey, not only on profile attributes. An abandoned-cart email can reference the cart from the event that started the journey."
      }), "\n", _jsx(_components.h2, {
        id: "whats-different",
        children: "What's different"
      }), "\n", _jsx(_components.p, {
        children: "You don't attach an audience or a schedule to the message. The journey decides who reaches the tile and when. Neither control appears on the message."
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/native-delivery/reporting",
          children: "Reporting"
        }), " is per message, not per send. You see how the email performs overall, but because a journey has no runs, there's no per-send breakdown and no ", _jsx(_components.strong, {
          children: "Skipped"
        }), " or ", _jsx(_components.strong, {
          children: "Expired"
        }), " columns."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "native-delivery/journeys/messages-reporting.png",
          alt: "The journey's Messages tab showing per-message engagement and a message preview"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Stopping works through the journey. Pausing or stopping the journey is what stops its emails. The campaign controls in ", _jsx(_components.a, {
          href: "/native-delivery/campaigns",
          children: "Send a campaign"
        }), " don't apply."]
      }), "\n", _jsx(_components.h2, {
        id: "subsets-on-the-parent-model",
        children: "Subsets on the parent model"
      }), "\n", _jsxs(_components.p, {
        children: ["If the parent model has ", _jsx(_components.a, {
          href: "/customer-studio/subsets",
          children: "subset categories"
        }), ", a ", _jsx(_components.strong, {
          children: "Send email"
        }), " tile applies the journey's subsets when it resolves recipients. An audience-based journey uses the entry audience's subsets. An event-based journey uses the subsets selected on its Start tile. See ", _jsx(_components.a, {
          href: "/customer-studio/journeys/create#subsets",
          children: "Subsets on event entry"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch checks the subsets again when each message is due to send. A member who stops matching after they enter the journey stays in the journey, but they aren't sent the message unless they match again before it expires."
      }), "\n", _jsx(_components.p, {
        children: "On a parent model with a required category, the send needs a selection for it. An event-based journey with none fails to send until you select one on the Start tile. The send also fails if a subset selected on the Start tile was deleted."
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/native-delivery/subscriptions-and-consent",
            children: "Subscriptions and consent"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/native-delivery/reporting",
            children: "Reporting"
          })
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
